// import React from "react";
import React, { useId, useLayoutEffect, useRef, useState } from "react";
import "../../styles/components/hud/GuiPopupBubbleView.css";

export type Bubble = {
  id: number;
  bubbleType: string;
  text: string;
  color: string;
};

interface BubbleViewProps {
	bubble: Bubble;
};

// interface BubbleStackViewProps {
// 	bubbles: Bubble[];
// };

type ParametricBadgeProps = {
  text: string;
  variant?: string;
  padX?: number;        // inner left/right padding
  minWidth?: number;    // clamp width for short labels
  height?: number;      // badge height (Figma = 48)
  stroke?: number;      // border thickness (Figma = 2)
  fillOpacity?: number; // 0..1 (Figma used 0.6)
};

const BASE_W = 212;     // Figma export width
const BASE_H = 48;      // Figma export height
const CAP_LEN = 21.4922; // left cap length in the original path (for sizing maths)

/** Build the path string for a given width by translating all right-side x's by dx. */
function buildPath(W: number): string {
  const dx = W - BASE_W;

  return [
    // Left/top run
    `M21.4922 1`,
    `H${W - 21.4922}`,  // Mirror left cap width on right
    
    // Top-right corner (mirror of top-left)
    `C${W - 18.2953} 1 ${W - 15.2565} 2.39111 ${W - 13.167} 4.81055`,
    
    // Right slant down (mirror of left slant up)
    `L${W - 3.99316} 15.4326`,
    
    // Curve along right side (mirror of left curve)
    `C${W - 0.646688} 19.3075 ${W - 0.415692} 24.9795 ${W - 3.43652} 29.1133`,
    
    // Bottom-right slant (mirror of bottom-left)
    `L${W - 13.2119} 42.4902`,
    
    // Bottom-right corner (mirror of bottom-left corner)
    `C${W - 15.2831} 45.3245 ${W - 18.5833} 47 ${W - 22.0938} 47`,
    
    // Bottom edge back to left
    `H22.0938`,
    
    // Left side (unchanged)
    `C18.5833 47 15.2831 45.3245 13.2119 42.4902`,
    `L3.43652 29.1133`,
    `C0.415692 24.9795 0.646688 19.3075 3.99316 15.4326`,
    `L13.167 4.81055`,
    `C15.2565 2.39111 18.2953 1 21.4922 1`,
    `Z`,
  ].join(" ");
}

const ParametricBadge: React.FC<ParametricBadgeProps> = ({
  text,
  variant = "green",
  padX = 16,
  minWidth = BASE_W,
  height = BASE_H,
  stroke = 2,
  fillOpacity = 0.6,
}) => {
  const measRef = useRef<HTMLSpanElement>(null);
  const [tw, setTw] = useState(0);
  const gradId = useId();

  // Measure label with same font we’ll draw
  useLayoutEffect(() => {
    if (measRef.current) setTw(Math.ceil(measRef.current.offsetWidth));
  }, [text]);

  // Size rule: fixed caps on both sides + stretchable center for text
  const innerNeeded = tw + padX * 2;
  const W = Math.max(minWidth, innerNeeded + CAP_LEN * 2);

  const [c0, c1] =
    variant === "red" ? ["#B7645E", "#654A48"] : ["#4D7354", "#324336"];

  return (
    <>
      {/* Hidden measurer */}
      <span
        ref={measRef}
				className="gui-popup-text-measurer"
      >
        {text}
      </span>

      <svg
        width={W}
        height={height}
        viewBox={`0 0 ${W} ${height}`}
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: "block" }}
      >
        <defs>
          <linearGradient id={gradId} x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor={c0} />
            <stop offset="100%" stopColor={c1} />
          </linearGradient>
        </defs>

        <path
          d={buildPath(W)}
          fill={`url(#${gradId})`}
          fillOpacity={fillOpacity}
          stroke="#C5C5C5"
          strokeWidth={stroke}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />

        <text
          x={W / 2}
          y={height / 2}
          dominantBaseline="middle"
          textAnchor="middle"
          fill="#fff"
          fontWeight={700}
          fontSize={16}
        >
          {text}
        </text>
      </svg>
    </>
  );
};

const GuiPopupBubbleView: React.FC<BubbleViewProps> = ({bubble}) => {
	return ( <ParametricBadge text={bubble.text} variant={bubble.color} />)
}

type BubbleStackViewProps = {
  bubbles?: Bubble[];
  className?: string;
  style?: React.CSSProperties;
};

const GuiPopupBubbleStackView = React.forwardRef<HTMLDivElement, BubbleStackViewProps>(
  ({ bubbles = [], className, style }, ref) => {
    return (
      <div
        ref={ref}
        className={`gui-popup-bubble-stack ${className ?? ""}`}
        style={style}
      >
        {bubbles.map((b) => (
          <GuiPopupBubbleView key={b.id} bubble={b} />
        ))}
      </div>
    );
  }
);

GuiPopupBubbleStackView.displayName = "GuiPopupBubbleStackView";

const GuiPopupBubbleStackViewOnly: React.FC = () => {
  const bubbles = [
    {
      id: 1,
      bubbleType: "bubble",
      text: "Empathetic",
      color: "green",
    },
    {
      id: 2,
      bubbleType: "bubble",
      text: "Too much info",
      color: "red",
    },
    {
      id: 3,
      bubbleType: "bubble",
      text: "Lack of clarity",
      color: "red",
    },
    {
      id: 4,
      bubbleType: "bubble",
      text: "A much longer caption that we might see I dunno.",
      color: "green",
    },
  ];
	
  return (
    <GuiPopupBubbleStackView bubbles={bubbles} />
  );
};

export default GuiPopupBubbleStackView;
export { GuiPopupBubbleStackViewOnly };